import { Injectable } from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {AngularFirestore, AngularFirestoreCollection} from '@angular/fire/compat/firestore';
import {UserService} from './user.service';
import {
  AssistanceProvided, AssistanceProvidedType,
  ExtensionActivity, ExtensionActivityType,
  ExtensionCategory, ExtensionEvent,
  Hort360GrowerProperty,
  Hort360PropertySummary,
  Meeting,
  PracticeChange
} from '../../../functions/src/types';
import {DataService, ResolveCategory} from './data.service';
import {PracticeChangeService, PracticeChangeType} from './practice-change.service';
import {PracticeChangeForm} from './practice-change.types';

@Injectable({
  providedIn: 'root'
})
export class ReportingService {
  private propertiesCollection: AngularFirestoreCollection<Hort360GrowerProperty>;
  private watchers: Subscription[] = [];
  private recentCollection: AngularFirestoreCollection;

  constructor(
    private fbDb: AngularFirestore,
    private dataService: DataService,
    private resolveCategory: ResolveCategory,
    private practiceChangeService: PracticeChangeService,
  ) { }

  async get(from: string, to: string, user: string = null) {
    // Results
    const results = new ReportRange(this.dataService.categories.getValue(),
                                    this.dataService.activityTypes.getValue(),
                                    this.practiceChangeService.types,
                                    this.dataService.meetings.getValue(),
                                    this.dataService.assistanceProvidedTypes.getValue()
    );

    // Load event based data;
    const eventPromise = new Promise<void>(async resolve => {
      try {
        const events = await this.fbDb.collection<ExtensionEvent>(this.dataService.eventsRef.ref, (ref) => {
          let qref = ref.where('date', '>=', +from)
                        .where('date', '<=', +to);
          if (user) {
            qref = qref.where('user', '==', user + '');
          }
          return qref;
        }).get().toPromise();
        results.events = events.docs.map(d => d.data());
      } catch (e) {
        console.error(e);
      }
      resolve();
    });

    // Load contacts data;
    const contactPromise = new Promise<void>(async resolve => {
      try {
        const contacts = await this.fbDb.collection<ExtensionActivity>(this.dataService.contactsRef.ref, (ref) => {
          let qref = ref.where('date', '>=', +from)
                        .where('date', '<=', +to);
          if (user) {
            qref = qref.where('user', '==', user + '');
          }
          return qref;
        }).get().toPromise();
        results.contacts = contacts.docs.map(d => d.data());
      } catch (e) {
        console.error(e);
      }
      resolve();
    });

    // Load meetings data;
    const meetingPromise = new Promise<void>(async resolve => {
      try {
        const meetings = await this.fbDb.collection<Meeting>(this.dataService.meetingsRef.ref, (ref) => {
          let mref = ref.where('date', '>=', +from)
            .where('date', '<=', +to);
          if (user) {
            mref = mref.where('user', '==', user + '');
          }
          return mref;
        }).get().toPromise();
        results.meetings = meetings.docs.map(d => d.data());
      } catch (e) {
        console.error(e);
      }
      resolve();
    });

    const assistanceProvidedPromise = new Promise<void>(async resolve => {
      try {
        const assistanceProvided = await this.fbDb.collection<AssistanceProvided>(this.dataService.assistanceRef.ref, (ref) => {
          let mref = ref.where('date', '>=', +from)
            .where('date', '<=', +to);
          if (user) {
            mref = mref.where('user', '==', user + '');
          }
          return mref;
        }).get().toPromise();
        results.assistanceProvided = assistanceProvided.docs.map(d => d.data());
      } catch (e) {
        console.error(e);
      }
      resolve();
    });

    const practiceChangePromise = new Promise<void>(async resolve => {
      try {
        const practiceChange = await this.fbDb.collection<PracticeChange>(this.dataService.practiceChangesRef.ref, (ref) => {
          let mref = ref.where('date', '>=', +from)
            .where('date', '<=', +to);
          if (user) {
            mref = mref.where('user', '==', user + '');
          }
          return mref;
        }).get().toPromise();
        results.practiceChanges = practiceChange.docs.map(d => d.data());
      } catch (e) {
        console.error(e);
      }
      resolve();
    });

    try {
      await Promise.all([eventPromise, contactPromise, meetingPromise, assistanceProvidedPromise, practiceChangePromise]);
      results.buildActivities();
    } catch (e) {
      console.error(e);
    }

    // Done!
    return results;
  }
}

export class ReportRange {
  private knownCategories: ExtensionCategory[] = [];
  private knownActivities: ExtensionActivityType[] = [];
  private knownPracticeChange: {[key: string]: PracticeChangeType } = {}; // XYZ123: {fieldId: pcSoil, etc. }
  private knownMeetings: Meeting[] = [];
  private knownAssistanceProvidedTypes: AssistanceProvidedType[] = [];

  dateFrom: string; // X
  dateTo: string;

  events: ExtensionEvent[] = [];
  contacts: ExtensionActivity[] = [];
  meetings: Meeting[] = [];
  assistanceProvided: AssistanceProvided[] = [];
  practiceChanges: PracticeChange[] = [];
  // INTERNAL;
  activities: ExtensionActivityItem[]; // PC + Activities

  constructor(knownCategories: ExtensionCategory[],
              knownActivities: ExtensionActivityType[],
              knownPracticeChange: {[key: string]: PracticeChangeType },
              knownMeetings: Meeting[],
              knownAssistanceProvidedTypes: AssistanceProvidedType[]) {
    this.knownCategories = knownCategories;
    this.knownActivities = knownActivities;
    this.knownPracticeChange = knownPracticeChange;
    this.knownMeetings = knownMeetings;
    this.knownAssistanceProvidedTypes = knownAssistanceProvidedTypes;
  }

  buildActivities() {
    const items: ExtensionActivityItem[][] = this.contacts.map(c => {
      const types: ExtensionActivityItem[] = [];
      Object.keys(c.activities).forEach(k => {
        const activityValue = c.activities[k];
        if (!activityValue) {
          return;
        }
        const activityType = this.knownActivities.find(a => a.id === k);
        const activityItem = {
          activityId: activityType.id,
          category: activityType.category,
          user: c.user,
          name: activityType.name,
          total: activityValue
        } as ExtensionActivityItem;
        types.push(activityItem);
      });
      Object.keys(this.knownPracticeChange).forEach(categoryId => {
        const pcType = this.knownPracticeChange[categoryId];
        const value: PracticeChangeForm = c[pcType.fieldId];
        if (value && value.completed) {
          const activityItem = {
            activityId: categoryId,
            category: categoryId,
            user: c.user,
            name: pcType.name,
            total: 1
          } as ExtensionActivityItem;
          types.push(activityItem);
        }
      });
      return types;
    });
    this.activities = [].concat(...items);
  }

  reportingFor(from: string, to: string): ReportRange {
    const result = new ReportRange(this.knownCategories, this.knownActivities, this.knownPracticeChange, this.knownMeetings, this.knownAssistanceProvidedTypes);
    result.events = this.events.filter(v => v.date >= +from && v.date <= +to);
    result.contacts = this.contacts.filter(v => v.date >= +from && v.date <= +to);
    result.meetings = this.meetings.filter(v => v.date >= +from && v.date <= +to);
    result.assistanceProvided = this.assistanceProvided.filter(v => v.date >= +from && v.date <= +to);
    result.practiceChanges = this.practiceChanges.filter(v => v.date >= +from && v.date <= +to);

    result.buildActivities();
    return result;
  }

  get totalEvents(): number {
    return this.events.length;
  }
  get totalAttendance(): number {
    return this.events.map(e => e.attendees).reduce((a, ca) => a += ca, 0);
  }
  get totalContacts(): number {
    return this.contacts.length;
  }
  get totalActivities(): number {
    return this.activities.length;
  }
  get totalMeetings(): number {
    return this.meetings.length;
  }
  get totalAssistanceProvided(): number {
    return this.assistanceProvided.length;
  }
  get totalPracticeChanges(): number {
    return this.practiceChanges.length;
  }
  get totalInteractions(): number {
    return this.totalActivities + this.totalAttendance + this.totalMeetings + this.totalAssistanceProvided + this.totalPracticeChanges;
  }
  get uniqueCategories(): string[] {
    let categories = this.activities.map(a => a.category);
    categories = categories.concat(this.events.map(e => e.category));
    return categories.filter((c, i, arr) => arr.indexOf(c) === i);
  }
  get uniqueUsers(): string[] {
    let users = this.activities.map(a => a.user + '');
    users = users.concat(this.events.map(e => e.user + ''));
    return users.filter((u, i, arr) => arr.indexOf(u) === i);
  }
  get uniqueUsersForMeetings(): string[] {
    return this.meetings.map(m => m.user + '').filter((u, i, arr) => arr.indexOf(u) === i);
  }
  get uniqueUsersForAssistance(): string[] {
    return this.assistanceProvided.map(m => m.user + '').filter((u, i, arr) => arr.indexOf(u) === i);
  }
  get uniqueAssistanceTypes(): string[] {
    let assistanceTypes = this.assistanceProvided.map(a => a.type);
    return assistanceTypes.filter((c, i, arr) => arr.indexOf(c) === i);
  }

  get uniqueUsersForPracticeChanges(): string[] {
    return this.practiceChanges.map(m => m.user + '').filter((u, i, arr) => arr.indexOf(u) === i);
  }
  get uniquePracticeChangeActivities(): string[] {
    let practiceChangeActivities = this.practiceChanges.map(p=>p.activity);
    return practiceChangeActivities.filter((c, i, arr) => arr.indexOf(c) === i);
  }

  get interactionsByActivity(): {[key: string]: number} {
    // Note to self; KEY here is a mess. Can be;
    //  - ActivityId
    //  - CategoryId (for Practice Change)
    //  - EventId

    const o = {};
    this.activities.forEach(a => {
      o[a.activityId] =
        this.activities.filter(e => e.activityId === a.activityId).map(e => e.total).reduce((t, ct) => t += ct, 0);
    });
    // Retrofit events?
    this.events.forEach(e => {
      o[e.id] = e.attendees;
    });
    return o;
  }
  get interactionsPerCategory(): {[key: string]: number} {
    const categories = this.uniqueCategories;
    const o = {};
    categories.forEach(c => {
      o[c] =
        this.events.filter(e => e.category === c).map(e => e.attendees).reduce((a, ca) => a += ca, 0) +
        this.activities.filter(e => e.category === c).map(e => e.total).reduce((t, ct) => t += ct, 0);
    });
    return o;
  }
  get interactionsPerUser(): {[key: string]: number} {
    const users = this.uniqueUsers;
    const o = {};
    users.forEach(u => {
      o[u] =
        this.events.filter(e => e.user === u).map(e => e.attendees).reduce((a, ca) => a += ca, 0) +
        this.activities.filter(e => e.user === u).map(e => e.total).reduce((t, ct) => t += ct, 0);
    });
    return o;
  }

  get meetingsWithOthers(): { [key: string]: number } {
    const users = this.uniqueUsersForMeetings;
    const o = {};
    users.forEach(u => {
      o[u] =
        this.meetings
          .filter(e => e.user === u)
          .map(e => e.attendees)
          .reduce((a, ca) => a += ca, 0)
    });
    return o;
  }

  get assistanceProvidedByUser(): {[key: string]: number} {
    console.log("Assistance:::",this.assistanceProvided);
    const users = this.uniqueUsersForAssistance;
    const o = {};
    users.forEach(u => {
      o[u] = this.assistanceProvided.filter(e => e.user === u).length;
    });
    return o;
  }

  get assistanceProvidedPerType(): {[key: string]: number} {
    console.log("Assistance:::",this.assistanceProvided);
    const assistanceTypes = this.uniqueAssistanceTypes;
    const o = {};
    assistanceTypes.forEach(u => {
      o[u] = this.assistanceProvided.filter(e => e.type === u).length;
    });
    return o;
  }

  get practiceChangesPerType(): {[key: string]: number} {
    console.log("PracticeChange:::",this.practiceChanges);
    const practiceChangeActivities = this.uniquePracticeChangeActivities;
    const o = {};
    practiceChangeActivities.forEach(u => {
      o[u] = this.practiceChanges.filter(e => e.activity === u).length;
    });
    return o;
  }

}

export interface ExtensionActivityItem {
  activityId: string;
  category: string;
  name: string;
  total: number;
  user: string;
}
